import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { bookingShape } from 'constants/propTypes';
import {
  BUSINESS_BENEFIT_UPGRADE,
  FREE_UPGRADE,
  IMPULSE_UPGRADE,
  TIER_BENEFIT_UPGRADE,
  UPGRADE_WITH_POINTS
} from 'constants/afterSalesShortcuts';
import config from 'utils/config';

import FreeUpgradeCta from '../FreeUpgradeCta';
import ImpulseUpgradeCta from '../ImpulseUpgradeCta';
import UpgradePointsCta from '../UpgradePointsCta';
import TierBenefitUpgradeCta from '../TierBenefitUpgradeCta';
import BusinessBenefitUpgradeCta from '../BusinessBenefitUpgradeCta';
import { CtaButton } from '../AfterSalesCtas.styles';
import { isExchangingASubsetOfPassengers } from 'utils/summatedFares';

import { StyledButtonMenuContainer, StyledIcon, StyledUpgradeButtonContainer, StyledUpgradeButtonText } from './UpgradeCta.styles';

const IS_TIER_BENEFIT_UPGRADE_ENABLED = config('ENABLE_TIER_BENEFIT_UPGRADE', { canOverride: true }) === 'true';
const IS_BUSINESS_BENEFIT_UPGRADE_ENABLED = config('ENABLE_BUSINESS_ACCOUNT_BENEFIT_UPGRADE', { canOverride: true }) === 'true';

const UpgradeCta = ({
  afterSalesShortcuts = [],
  booking,
  exchangeWillCreateChildrenAlone = false,
  isExchangingSubsetOfPaxsInPALBooking = false,
  selectedItemRefs = [],
  disabled = false,
  isMigratedMultiOrDisabled = false,
  isAnySelectedTicketPrrAttempted = false,
  areItemsInASummatedFare = false,
  areItemsInAThroughFare = false,
  isPurchasedWithSubAndSubNotActive = false,
  isEurostarRedAndBlue = false,
  isExchangedUpgradedCancelledDisabled = false,
  isAnySelectedTicketPrrRefunded = false
}) => {
  const [isButtonMenuOpen, setButtonMenuOpen] = useState(false);
  const [isImpulseDisabled, setIsImpulseDisabled] = useState(disabled);
  const [isUpgradePointsDisabled, setIsUpgradePointsDisabled] = useState(disabled);
  const [isTierBenefitUpgradeDisabled, setIsTierBenefitUpgradeDisabled] = useState(disabled);
  const [isBusinessBenefitDisabled, setIsBusinessBenefitDisabled] = useState(disabled);
  const [isFreeUpgradeDisabled, setIsFreeUpgradeDisabled] = useState(disabled);

  const {
    shouldShowUpgradePointsCta,
    shouldShowFreeUpgradeCta,
    shouldShowImpulseUpgradeCta,
    shouldShowTierBenefitUpgradeCta,
    shouldShowBusinessBenefitUpgradeCta
  } = useMemo(() => {
    let shouldShowUpgradePointsCta = false;
    let shouldShowFreeUpgradeCta = false;
    let shouldShowImpulseUpgradeCta = false;
    let shouldShowTierBenefitUpgradeCta = false;
    let shouldShowBusinessBenefitUpgradeCta = false;

    if (afterSalesShortcuts.length > 0) {
      afterSalesShortcuts.forEach((afterSales) => {
        switch (afterSales) {
          case FREE_UPGRADE:
            shouldShowFreeUpgradeCta = true;
            break;
          case IMPULSE_UPGRADE:
            shouldShowImpulseUpgradeCta = true;
            break;
          case UPGRADE_WITH_POINTS:
            shouldShowUpgradePointsCta = true;
            break;
          case TIER_BENEFIT_UPGRADE:
            shouldShowTierBenefitUpgradeCta = IS_TIER_BENEFIT_UPGRADE_ENABLED;
            break;
          case BUSINESS_BENEFIT_UPGRADE:
            shouldShowBusinessBenefitUpgradeCta = IS_BUSINESS_BENEFIT_UPGRADE_ENABLED;
            break;
          default:
            break;
        }
      });
    }

    return {
      shouldShowUpgradePointsCta,
      shouldShowFreeUpgradeCta,
      shouldShowImpulseUpgradeCta,
      shouldShowTierBenefitUpgradeCta,
      shouldShowBusinessBenefitUpgradeCta
    };
  }, [afterSalesShortcuts]);

  if (
    !shouldShowUpgradePointsCta &&
    !shouldShowFreeUpgradeCta &&
    !shouldShowImpulseUpgradeCta &&
    !shouldShowTierBenefitUpgradeCta &&
    !shouldShowBusinessBenefitUpgradeCta
  ) {
    return null;
  }

  const isExchangingASubsetOfPax =
    isExchangingASubsetOfPassengers(booking.outbound, selectedItemRefs) ||
    isExchangingASubsetOfPassengers(booking.inbound, selectedItemRefs);
  const isABSBoooking = [...booking.outbound, ...booking.inbound].some(({ addOnLeg }) => !!addOnLeg);

  const isUpgradeWithPointsDisable =
    (areItemsInASummatedFare && !isEurostarRedAndBlue) ||
    isPurchasedWithSubAndSubNotActive ||
    (areItemsInAThroughFare && !isEurostarRedAndBlue) ||
    isAnySelectedTicketPrrAttempted;
  const isFreeUpgradeDisable =
    areItemsInASummatedFare ||
    areItemsInAThroughFare ||
    isABSBoooking ||
    isExchangedUpgradedCancelledDisabled ||
    isExchangingASubsetOfPax ||
    isAnySelectedTicketPrrAttempted ||
    isAnySelectedTicketPrrRefunded;
  const isImpulseUpgradeDisable =
    areItemsInASummatedFare || areItemsInAThroughFare || isMigratedMultiOrDisabled || isAnySelectedTicketPrrAttempted;

  const disabledImpulseUpgrade = isImpulseUpgradeDisable || isImpulseDisabled || disabled;
  const disabledUpgradePoints = isUpgradeWithPointsDisable || isUpgradePointsDisabled || disabled;
  const disabledTierBenefitUpgrade = isTierBenefitUpgradeDisabled || disabled;
  const disabledBusinessBenefit = isBusinessBenefitDisabled || disabled;
  const disabledFreeUpgrade = isFreeUpgradeDisable || isFreeUpgradeDisabled || disabled;

  const upgradeButtons = [
    shouldShowImpulseUpgradeCta && { disabled: disabledImpulseUpgrade },
    shouldShowUpgradePointsCta && { disabled: disabledUpgradePoints },
    shouldShowTierBenefitUpgradeCta && { disabled: disabledTierBenefitUpgrade },
    shouldShowBusinessBenefitUpgradeCta && { disabled: disabledBusinessBenefit },
    shouldShowFreeUpgradeCta && { disabled: disabledFreeUpgrade }
  ].filter(Boolean);
  const isUpgradeAccordionDisabled =
    selectedItemRefs?.length === 0 || upgradeButtons.every(({ disabled: isButtonDisabled }) => isButtonDisabled);

  const handleToggleButtonMenu = () => setButtonMenuOpen((isOpen) => !isOpen);

  return (
    <>
      <CtaButton
        isMenuOpen={isButtonMenuOpen}
        disabled={isUpgradeAccordionDisabled}
        type="button"
        data-cy="upgrade-cta"
        label={
          <StyledUpgradeButtonContainer isMenuOpen={isButtonMenuOpen}>
            <StyledUpgradeButtonText>
              <FormattedMessage id="afterSales.upgrade" />
            </StyledUpgradeButtonText>
            <StyledIcon isMenuOpen={isButtonMenuOpen} />
          </StyledUpgradeButtonContainer>
        }
        onClick={handleToggleButtonMenu}
      />
      <StyledButtonMenuContainer isMenuOpen={isButtonMenuOpen}>
        {shouldShowImpulseUpgradeCta && (
          <ImpulseUpgradeCta
            disabled={disabledImpulseUpgrade}
            selectedItemRefs={selectedItemRefs}
            booking={booking}
            label={<FormattedMessage id="afterSales.impulseUpgrade" />}
            willResultInChildAlone={exchangeWillCreateChildrenAlone}
            isMenuOpen={isButtonMenuOpen}
            setDisabled={setIsImpulseDisabled}
          />
        )}
        {shouldShowUpgradePointsCta && (
          <UpgradePointsCta
            disabled={disabledUpgradePoints}
            selectedItemRefs={selectedItemRefs}
            booking={booking}
            label={<FormattedMessage id="afterSales.upgradePoints" />}
            willResultInChildAlone={exchangeWillCreateChildrenAlone}
            exchangingSubsetOfPaxsInPALBooking={isExchangingSubsetOfPaxsInPALBooking}
            isMenuOpen={isButtonMenuOpen}
            setDisabled={setIsUpgradePointsDisabled}
          />
        )}
        {shouldShowTierBenefitUpgradeCta && (
          <TierBenefitUpgradeCta
            selectedItemRefs={selectedItemRefs}
            disabled={disabledTierBenefitUpgrade}
            booking={booking}
            label={<FormattedMessage id="afterSales.tierBenefitUpgradeCta" />}
            isMenuOpen={isButtonMenuOpen}
            setDisabled={setIsTierBenefitUpgradeDisabled}
            areItemsInASummatedFare={areItemsInASummatedFare}
          />
        )}
        {shouldShowBusinessBenefitUpgradeCta && (
          <BusinessBenefitUpgradeCta
            selectedItemRefs={selectedItemRefs}
            disabled={disabledBusinessBenefit}
            booking={booking}
            label={<FormattedMessage id="afterSales.businessBenefitUpgradeCta" />}
            isMenuOpen={isButtonMenuOpen}
            setDisabled={setIsBusinessBenefitDisabled}
          />
        )}
        {shouldShowFreeUpgradeCta && (
          <FreeUpgradeCta
            disabled={disabledFreeUpgrade}
            selectedItemRefs={selectedItemRefs}
            booking={booking}
            label={<FormattedMessage id="afterSales.freeUpgradeCta" />}
            isMenuOpen={isButtonMenuOpen}
            setDisabled={setIsFreeUpgradeDisabled}
          />
        )}
      </StyledButtonMenuContainer>
    </>
  );
};

UpgradeCta.propTypes = {
  afterSalesShortcuts: PropTypes.arrayOf(PropTypes.string),
  booking: bookingShape,
  exchangeWillCreateChildrenAlone: PropTypes.bool,
  isExchangingSubsetOfPaxsInPALBooking: PropTypes.bool,
  isUpgradeWithPointsDisable: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  isMigratedMultiOrDisabled: PropTypes.bool,
  isAnySelectedTicketPrrAttempted: PropTypes.bool,
  areItemsInASummatedFare: PropTypes.bool,
  areItemsInAThroughFare: PropTypes.bool,
  isPurchasedWithSubAndSubNotActive: PropTypes.bool,
  isEurostarRedAndBlue: PropTypes.bool,
  isExchangedUpgradedCancelledDisabled: PropTypes.bool,
  isAnySelectedTicketPrrRefunded: PropTypes.bool
};

export default UpgradeCta;
